import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import AssaysViewBatchModalComponent from '@/components/assays-view/components/batch/batch-modal/assays-view-batch-modal.vue'
import { AssaysViewSampleNodeStructure } from '@/components/assays-view/components/modal/assays-view-modal.model'
import {
    AssayBatchModalState,
    initialAssayBatchModalState
} from '@/components/assays-view/components/batch/batch-modal-assays/assays-view-batch-modal-assays.model'
import { CommonPriorityResponse } from '@/api/services/common/priority/common-priority.dto'
import { AssayModalAssayWithValuableConceptResponse } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.dto'
import { AssaysViewPortionResponse } from '@/components/assays-view/services/assays-view-portion/assays-view-portion.dto'
import { AssaysViewApplyAssaysRequest } from '@/components/assays-view/services/assays-view-subportion/assays-view-subportion.dto'
import { assaysViewSubportionService } from '@/components/assays-view/services/assays-view-subportion/assays-view-subportion.service'
import { assaysViewAssayService } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.service'
import { cloneDeep, intersection, isEqual, uniq } from 'lodash'
import { BatchItemWrapper } from '@/components/assays-view/components/batch/batch-modal/assays-view-batch-modal.model'

@Component({
    components: {
        AssaysViewBatchModalComponent
    }
})
export default class AssaysViewBatchModalAssays extends Vue {
    @Prop({ type: Number, required: true }) readonly casId!: number
    @Prop({ type: Object }) readonly casePriority!: CommonPriorityResponse
    @Prop({ type: Array, default: () => [] }) readonly sampleNodes!: AssaysViewSampleNodeStructure[]

    @Ref() readonly batchModal!: any

    state: AssayBatchModalState

    @Watch('state.selectedPortions')
    async onSelectedPortionsChange(): Promise<void> {
        await this._loadAssaysIfNecessary()
    }

    constructor() {
        super()
        this.state = initialAssayBatchModalState()
    }

    private get _labId(): number {
        return this.$store.getters.labId
    }

    /// OPERATIONS ///

    private async _getAssays(laboratoryId: number): Promise<AssayModalAssayWithValuableConceptResponse[]> {
        const response =
            await this.$_toCallSecure(
                assaysViewAssayService.getAssays(laboratoryId))
        return response ?? []
    }
    private async _loadAssaysIfNecessary(): Promise<void> {
        this.state.loading = true
        try {
            if (this.state.selectedPortions.length) {
                const labIds = uniq(this.state.selectedPortions.map(portion => portion.currentLaboratory.labId))
                const labIdsSorted = [...labIds].sort((a, b) => (a - b))
                if (!isEqual(labIdsSorted, this.state.labIds)) {
                    let response: AssayModalAssayWithValuableConceptResponse[] = []
                    for (let i = 0; i < labIds.length; i++) {
                        const assays = await this._getAssays(labIds[i])
                        if (i === 0) {
                            response = assays
                        } else {
                            // TODO: backend should do the intersection (reduce logic in front)
                            response = intersection(response, assays)
                        }
                    }
                    this.state.labIds = labIds
                    this.state.assays = response
                }
            }
        } finally {
            this.state.loading = false
        }
    }

    batchItemIdResolver(assay: AssayModalAssayWithValuableConceptResponse): string {
        return `${assay.id}`
    }
    batchItemNameResolver(assay: AssayModalAssayWithValuableConceptResponse): string {
        return `${assay.name} - ${assay.assaygroup.name}`
    }
    batchItemFilter(batchItems: AssayModalAssayWithValuableConceptResponse[], filter: string): AssayModalAssayWithValuableConceptResponse[] {
        return batchItems.filter(batchItem =>
            batchItem.name.toLowerCase().includes(filter.toLowerCase()) ||
            batchItem.assaygroup.name.toLowerCase().includes(filter.toLowerCase()))
    }

    async save(selectedBatchItemWrappers: BatchItemWrapper<AssayModalAssayWithValuableConceptResponse>[]): Promise<void> {
        const request: AssaysViewApplyAssaysRequest = {
            assays:
                selectedBatchItemWrappers.map(batchItemWrapper => ({
                    assayId: batchItemWrapper.batchItem.id,
                    priorityId: batchItemWrapper.priority.id
                })),
            hierarchicalPortionTreeRequest:
                this.state.selectedPortions.map(portion => ({
                    sampleId: portion.sampleId,
                    portionId: portion.portionId
                }))
        }
        const response =
            await this.$_toCallUnique(
                assaysViewSubportionService.createSubportions(
                    this._labId,
                    this.casId,
                    this.state.numberOfCopies,
                    request))
        if (response) {
            this.$_showSuccessModal(this.$tc('component.assaysViewModal.assaysCreatedSuccess'))
            this.$emit('create')
            this.batchModal.hide()
        }
    }

    /// MODAL ///

    async show(selectedPortions: AssaysViewPortionResponse[]): Promise<void> {
        this.state = initialAssayBatchModalState()
        this.state.selectedPortions = cloneDeep(selectedPortions)
        await this.batchModal.show()
    }
    hide(): void {
        this.batchModal.hide()
    }
}
